/**
 * Business Profile Card
 * Displays business profile information summary
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Building2, Edit, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BusinessProfileCard = ({ businessProfile, isExpanded = true, onToggle }) => {
  const navigate = useNavigate();

  // Helper to calculate business age
  const getBusinessAge = (yearFounded) => {
    if (!yearFounded) return null;
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(yearFounded);
    return age > 0 ? `${age} years` : 'New';
  };

  // Helper to safely parse owners array
  const getOwnersArray = () => {
    if (!businessProfile?.owners) return [];

    // If it's already an array, return it
    if (Array.isArray(businessProfile.owners)) {
      return businessProfile.owners;
    }

    // If it's a JSON string, parse it
    if (typeof businessProfile.owners === 'string') {
      try {
        const parsed = JSON.parse(businessProfile.owners);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Error parsing owners:', e);
        return [];
      }
    }

    return [];
  };

  const owners = getOwnersArray();

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onToggle}
            className="flex items-center space-x-2 flex-1 text-left hover:opacity-80 transition"
          >
            <ChevronDown
              className={`text-purple-600 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
              size={18}
            />
            <Building2 className="text-purple-600" size={20} />
            <h3 className="text-base font-bold text-gray-900">Business Profile</h3>
          </button>
          <button
            onClick={() => navigate('/business-profile?section=business')}
            className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
            title="Edit Business Profile"
          >
            <Edit size={16} />
          </button>
        </div>

      {isExpanded && (
        <div>
          {businessProfile ? (
            <div className="space-y-1 text-xs">
          <div className="flex justify-between items-start gap-2 py-0.5">
            <span className="text-gray-600 flex-shrink-0">Business:</span>
            <span className="font-semibold text-right">{businessProfile.business_name || 'N/A'}</span>
          </div>
          <div className="flex justify-between items-start gap-2 py-0.5">
            <span className="text-gray-600 flex-shrink-0">Industry:</span>
            <span className="font-semibold text-right">{businessProfile.industry || 'N/A'}</span>
          </div>
          <div className="flex justify-between items-start gap-2 py-0.5">
            <span className="text-gray-600 flex-shrink-0">Entity Type:</span>
            <span className="font-semibold text-right">{businessProfile.registration_type || businessProfile.business_structure || 'N/A'}</span>
          </div>
          {businessProfile.employees && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Employees:</span>
              <span className="font-semibold text-right">{businessProfile.employees}</span>
            </div>
          )}
          {businessProfile.year_founded && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Founded:</span>
              <span className="font-semibold text-right">{businessProfile.year_founded} ({getBusinessAge(businessProfile.year_founded)})</span>
            </div>
          )}
          {businessProfile.primary_location && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Location:</span>
              <span className="font-semibold text-right">{businessProfile.primary_location}</span>
            </div>
          )}
          {businessProfile.primary_market && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Market:</span>
              <span className="font-semibold text-right">{businessProfile.primary_market}</span>
            </div>
          )}
          {businessProfile.exit_horizon && (
            <div className="flex justify-between items-start gap-2 border-t border-gray-200 pt-1 mt-1">
              <span className="text-gray-600 flex-shrink-0">Exit Timeline:</span>
              <span className="font-semibold text-blue-700 text-right">{businessProfile.exit_horizon}</span>
            </div>
          )}
          {businessProfile.preferred_exit_type && (
            <div className="flex justify-between items-start gap-2 py-0.5">
              <span className="text-gray-600 flex-shrink-0">Exit Type:</span>
              <span className="font-semibold text-blue-700 text-right">{businessProfile.preferred_exit_type}</span>
            </div>
          )}
          {owners.length > 0 && (
            <div className="border-t border-gray-200 pt-1 mt-1">
              <div className="flex justify-between items-start gap-2 mb-0.5">
                <span className="text-gray-600 font-semibold flex-shrink-0">Owners:</span>
                <span className="text-xs text-gray-500 text-right">{owners.length} total</span>
              </div>
              {owners.map((owner, index) => (
                <div key={index} className="flex justify-between items-start gap-2 text-xs ml-2 py-0.5">
                  <span className="text-gray-700">{owner.name || 'Unnamed'}</span>
                  <span className="font-semibold text-right">{owner.ownership_percentage ? `${owner.ownership_percentage}%` : ''}</span>
                </div>
              ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-500">No business profile available</p>
        )}
      </div>
      )}
      </div>
    </div>
  );
};

BusinessProfileCard.propTypes = {
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  businessProfile: PropTypes.shape({
    business_name: PropTypes.string,
    industry: PropTypes.string,
    exit_horizon: PropTypes.string,
    business_structure: PropTypes.string,
    registration_type: PropTypes.string,
    employees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    year_founded: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    primary_location: PropTypes.string,
    primary_market: PropTypes.string,
    preferred_exit_type: PropTypes.string,
    owners: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        ownership_percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        phone: PropTypes.string
      })
    )
  })
};

export default BusinessProfileCard;
